import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { supabase } from '../lib/supabase';

type DashboardTheme = 'light' | 'dark';

type ContextValue = {
  theme: DashboardTheme;
  setTheme: (theme: DashboardTheme) => void;
  isAdmin: boolean;
};

const Context = createContext<ContextValue | null>(null);

const STORAGE_KEY = 'dashboard_theme';
const SETTINGS_KEY = 'dashboard_theme';

export function DashboardThemeProvider({
  children,
  isAdmin,
}: {
  children: ReactNode;
  isAdmin: boolean;
}) {
  const [theme, setThemeState] = useState<DashboardTheme>(() => {
    if (typeof window === 'undefined') return 'dark';
    const stored = localStorage.getItem(STORAGE_KEY) as DashboardTheme | null;
    return stored === 'light' || stored === 'dark' ? stored : 'dark';
  });

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    supabase
      .from('settings')
      .select('value')
      .eq('key', SETTINGS_KEY)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (!error && data?.value) {
          const v = (data.value as string).toLowerCase();
          if (v === 'light' || v === 'dark') {
            setThemeState(v);
            localStorage.setItem(STORAGE_KEY, v);
          }
        }
      });
    return () => { cancelled = true; };
  }, [isAdmin]);

  const setTheme = useCallback(
    (next: DashboardTheme) => {
      setThemeState(next);
      localStorage.setItem(STORAGE_KEY, next);
      if (isAdmin) {
        supabase
          .from('settings')
          .upsert(
            { key: SETTINGS_KEY, value: next, updated_at: new Date().toISOString() },
            { onConflict: 'key' }
          )
          .then(() => {});
      }
    },
    [isAdmin]
  );

  return (
    <Context.Provider value={{ theme, setTheme, isAdmin }}>
      {children}
    </Context.Provider>
  );
}

export function useDashboardTheme() {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useDashboardTheme must be used within DashboardThemeProvider');
  return ctx;
}
